import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, ArrowSquareOut, Code, ShieldCheck, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const DOCS_URL = `${BACKEND}/api/docs`;
const REDOC_URL = `${BACKEND}/api/redoc`;
const OPENAPI_URL = `${BACKEND}/api/openapi.json`;
const POSTMAN_URL = `${BACKEND}/api/admin/postman/collection.json`;

/**
 * Superadmin-only API Explorer.
 *
 * Iframes FastAPI's built-in Swagger UI at /api/docs. This shipped in
 * V1.5.8.6 alongside the candidates.py/reports.py refactor once the
 * OpenAPI schema was un-broken (StaleCvNudgeIn hoist). Handy for
 * integration-partner onboarding and internal API debugging without
 * exposing /api/docs publicly on the marketing site.
 */
export default function ApiExplorer() {
    const [copied, setCopied] = useState(false);
    const [view, setView] = useState("swagger"); // "swagger" | "redoc"

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(OPENAPI_URL);
            setCopied(true);
            toast.success("openapi.json URL copied — hand this to integration partners.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy to clipboard — long-press the URL below to copy manually.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="api-explorer-page">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <Link
                        to="/superadmin"
                        data-testid="api-explorer-back"
                        className="text-[11px] font-black uppercase tracking-wider text-[#525252] hover:text-ink inline-flex items-center gap-1.5 mb-3"
                    >
                        <ArrowLeft size={12} weight="bold" /> Superadmin
                    </Link>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1 inline-flex items-center gap-1.5">
                        <ShieldCheck size={12} weight="fill" /> Platform-level access
                    </p>
                    <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-2.5">
                        <Code size={28} weight="bold" /> API Explorer
                    </h1>
                    <p className="text-sm text-[#525252] mt-1 max-w-2xl">
                        Live Swagger UI for the NaukriDaddy backend — try any endpoint, inspect payloads, or copy the OpenAPI spec URL for integration partners.
                    </p>
                </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white border border-[#E5E5E5] p-4 mb-4 flex flex-wrap items-center gap-3">
                <div className="inline-flex border border-[#E5E5E5]" role="tablist">
                    <button
                        onClick={() => setView("swagger")}
                        data-testid="api-explorer-swagger-tab"
                        className={`px-4 py-2 text-[11px] font-black uppercase tracking-wider transition ${
                            view === "swagger"
                                ? "bg-ink text-white"
                                : "bg-white text-[#525252] hover:bg-[#FAFAFA]"
                        }`}
                    >
                        Swagger
                    </button>
                    <button
                        onClick={() => setView("redoc")}
                        data-testid="api-explorer-redoc-tab"
                        className={`px-4 py-2 text-[11px] font-black uppercase tracking-wider transition border-l border-[#E5E5E5] ${
                            view === "redoc"
                                ? "bg-ink text-white"
                                : "bg-white text-[#525252] hover:bg-[#FAFAFA]"
                        }`}
                    >
                        ReDoc
                    </button>
                </div>

                <div className="flex-1 min-w-[240px]">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-1">
                        OpenAPI spec
                    </p>
                    <code className="text-xs font-mono text-[#525252] break-all" data-testid="api-explorer-openapi-url">
                        {OPENAPI_URL}
                    </code>
                </div>

                <button
                    onClick={copy}
                    data-testid="api-explorer-copy-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-black uppercase tracking-wider border border-[#E5E5E5] hover:bg-[#FAFAFA] transition"
                >
                    {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                    {copied ? "Copied" : "Copy URL"}
                </button>

                <a
                    href={view === "redoc" ? REDOC_URL : DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="api-explorer-open-tab"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-black uppercase tracking-wider bg-ink text-white hover:bg-cobalt transition"
                >
                    <ArrowSquareOut size={12} weight="bold" /> Open in tab
                </a>

                {/* V1.5.8.16 — Postman v2.1 collection download */}
                <a
                    href={POSTMAN_URL}
                    data-testid="api-explorer-postman-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-black uppercase tracking-wider bg-splash text-ink hover:bg-splash/80 transition"
                    title="Download Postman v2.1 collection — import via Postman → File → Import"
                >
                    <DownloadSimple size={12} weight="bold" /> Postman collection
                </a>
            </div>

            {/* Docs iframe */}
            <div className="bg-white border border-[#E5E5E5]" data-testid="api-explorer-iframe-wrap">
                <iframe
                    key={view /* re-mount when swapping views */}
                    src={view === "redoc" ? REDOC_URL : DOCS_URL}
                    title="NaukriDaddy API Docs"
                    data-testid="api-explorer-iframe"
                    className="w-full block"
                    style={{ height: "calc(100vh - 320px)", minHeight: "600px", border: "none" }}
                />
            </div>

            <p className="text-[11px] text-[#A3A3A3] mt-4 max-w-3xl">
                Note: Swagger&apos;s <em>Try it out</em> uses your logged-in browser session (JWT cookie), so any endpoint you can call from the app you can call here — including destructive ones. Handle with care.
                <br />
                The <b>Postman collection</b> download bundles every endpoint into folders by tag; import via Postman → File → Import → drag the JSON. Set the <code>baseUrl</code> variable to your deployment after import.
            </p>
        </div>
    );
}
